import React from 'react';
import { formatTimeWithTimezone } from '../../utils/dateUtils';
import useLongPress from '../../hooks/useLongPress';
import BubbleStatusIcon from './BubbleStatusIcon';
import BubbleActionMenu from './BubbleActionMenu';

/**
 * ChatBubble — renders a single chat message with:
 *  - Customisable bubble colours / text size
 *  - Status tick icons (pending / sent / delivered / read)
 *  - Long-press to enter multi-select mode
 *  - Selection checkbox ring
 *  - Action pill menu (Edit / Delete / Select)
 */
const ChatBubble = ({
  message,
  currentUserUid,
  customization,
  senderAvatar,     // kept in props for potential future avatar use
  receiverAvatar,   // kept in props for potential future avatar use
  onEditRequest,
  onDelete,
  isSelectMode,
  isSelected,
  onLongPress,
  onSelectToggle,
  showActions,
  onToggleActions,
  selectedCount = 0,
}) => {
  // ── Normalise message shape ───────────────────────────────────────────────
  const content   = message.content ?? message.text;
  const timestamp = message.timestamp;
  const senderUid = message.sender_uid ?? message.senderId;
  const isDeleted = message.isDeleted;
  const isEdited  = message.edited;

  const isSent = currentUserUid
    ? senderUid === currentUserUid
    : senderUid === 'user';

  // ── Long-press handlers (touch + mouse) ───────────────────────────────────
  const longPressHandlers = useLongPress(
    () => onLongPress?.(message.id),
    { disabled: !!(isSelectMode || isDeleted || !isSent) }
  );

  // ── Click / tap logic ─────────────────────────────────────────────────────
  const handleBubbleClick = (e) => {
    if (isSelectMode) {
      if (isDeleted || !isSent) return;
      e.stopPropagation();
      onSelectToggle?.(message.id);
    } else if (isSent && !isDeleted && onEditRequest && onDelete) {
      onToggleActions?.(message.id);
    }
  };

  // ── Action-menu callbacks ─────────────────────────────────────────────────
  const handleEdit = (e) => {
    e.stopPropagation();
    onToggleActions?.(null);
    onEditRequest?.(message);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onToggleActions?.(null);
    onDelete?.(message.id);
  };

  const handleSelect = (e) => {
    e.stopPropagation();
    onLongPress?.(message.id);
  };

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    if (isDeleted) return;
    onSelectToggle?.(message.id);
  };

  // ── Styling ───────────────────────────────────────────────────────────────
  const bubbleColor = isSent ? customization.senderBubbleColor   : customization.receiverBubbleColor;
  const textColor   = isSent ? customization.senderTextColor     : customization.receiverTextColor;

  return (
    <div
      className="flex items-center w-full gap-3 px-1 transition-all duration-200"
      style={{ marginBottom: `${customization.messageGap}px` }}
    >
      {/* Selection Checkbox */}
      {isSelectMode && isSent && !isDeleted && (
        <div
          onClick={handleCheckboxClick}
          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
            isSelected
              ? 'border-blue-600 bg-blue-600 scale-110 shadow-sm'
              : 'border-gray-300 bg-white hover:border-blue-400'
          }`}
        >
          {isSelected && (
            <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
      )}

      {/* Bubble container */}
      <div
        className={`flex-1 flex flex-col ${isSent ? 'items-end' : 'items-start'}`}
        {...longPressHandlers}
      >
        {/* Bubble row */}
        <div className={`flex items-end gap-2 max-w-[85%] ${isSent ? 'flex-row' : 'flex-row-reverse'}`}>
          {/* Metadata (Outside) */}
          <div className={`flex flex-col mb-1 ${isSent ? 'items-end' : 'items-start'} min-w-fit select-none`}>
            <div className="flex items-center gap-1">
              {isEdited && !isDeleted && (
                <span className="text-[9px] italic text-gray-400 leading-none">
                  (edited)
                </span>
              )}
              <span className="text-[10px] font-medium text-gray-400 leading-none">
                {formatTimeWithTimezone(timestamp)}
              </span>
              {isSent && (
                <span className="flex-shrink-0">
                  <BubbleStatusIcon status={message.status} tickColor={customization.tickColor} />
                </span>
              )}
            </div>
          </div>

          {/* Bubble */}
          <div
            className={`rounded-2xl px-3 py-2 shadow-sm animate-chatFadeIn cursor-pointer select-none transition-all duration-200 ${
              isSelected ? 'ring-2 ring-blue-500 ring-offset-1' : ''
            }`}
            style={{
              backgroundColor: bubbleColor,
              borderRadius: isSent ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
            }}
            onClick={handleBubbleClick}
          >
            <p
              className={`break-words leading-relaxed ${isDeleted ? 'italic opacity-60' : ''}`}
              style={{ color: textColor, fontSize: `${customization.textSize}px` }}
            >
              {content}
            </p>
          </div>
        </div>

        {/* Action pill menu — own, non-deleted messages only */}
        {isSent && !isDeleted && showActions && (
          <BubbleActionMenu
            onEdit={handleEdit}
            onDelete={handleDelete}
            onSelect={handleSelect}
            selectedCount={selectedCount}
          />
        )}
      </div>
    </div>
  );
};

export default ChatBubble;
